// ---------------------------------------- REUSABLE RAZORPAY CHECKOUT --------------------------------------------

import { useEffect, useState } from "react";
import { API_URL } from "./constants";
import { useUserDetails } from "./UserDetailsContext";

const RazorpayCheckout = ({
  amount = 299,
  currency = "INR",
  label = "Purchase Credits",
  onSuccess = null,
  isScaleMode = false
}) => {
  const { userDetails, setUserDetails, token } = useUserDetails();
  const [showPopup, setShowPopup] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingPayment, setLoadingPayment] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const createOrder = async () => {
    if (!agreeToTerms) {
      alert("Please agree to the Refund Policy before proceeding.");
      return;
    }

    setLoadingOrder(true);

    try {
      const response = await fetch(`${API_URL}/api/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ amount, currency })
      });

      const data = await response.json();
      setLoadingOrder(false);
      launchRazorpay(data.id, data.razorpay_key_id);
    } catch (error) {
      setLoadingOrder(false);
      console.log(error);
      alert("Error creating order. Please try again later.");
    }
  };

  const launchRazorpay = (orderId, keyId) => {
    if (!window.Razorpay) {
      alert("Razorpay SDK failed to load. Check your internet connection.");
      return;
    }

    setLoadingPayment(true);

    const options = {
      key: keyId, 
      amount: amount, // Razorpay uses paisa (cents)
      currency: currency,
      name: "DataCreator AI",
      order_id: orderId,
      handler: async function (response) {
        try {
          const res = await fetch(`${API_URL}/api/verify-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
            body: JSON.stringify(response),
          });

          const data = await res.json();

          if (data.success) {
            setUserDetails({ ...userDetails, credits: data.credits });

            // Dispatch event for components like PaymentStatus
            const event = new CustomEvent("payment.captured", { detail: response });
            document.dispatchEvent(event);

            if (onSuccess) {
              await onSuccess(data);
            } else {
              alert("Your payment has been successful. Please proceed with your data generation.");
            }
          } else {
            alert("Your payment has failed. Please try again later or contact us.");
          }
        } catch (error) {
          console.error("Payment Verification Error:", error);
          alert("Error verifying payment. Please contact support.");
        } finally {
          setLoadingPayment(false);
          setLoadingOrder(false);
        }
      },
      prefill: {
        name: userDetails?.name || null,
        email: userDetails?.email || null,
        contact: null,
      },
      modal: {
        ondismiss: function () {
          setLoadingPayment(false);
          alert("Payment was closed before completing.");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  return (
    <>
      <button
        className={`${isScaleMode ? "flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-lg transition-all border border-amber-300 shadow-sm" : "bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition-all duration-200 flex items-center justify-center"}`}
        onClick={() => setShowPopup(true)}
        disabled={loadingOrder || loadingPayment}
      >
        {loadingOrder || loadingPayment ? (
          <span className="animate-spin border-4 border-current border-t-transparent rounded-full w-5 h-5 mr-2"></span>
        ) : null}
        {label}
        {isScaleMode && <span className="ml-1 px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded text-[10px] font-bold leading-none">Paid</span>}
      </button>

      {showPopup && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full animate-in fade-in zoom-in duration-200">
            <h2 className="text-xl font-bold mb-4 text-gray-900">{isScaleMode ? "Review Your Order" : "No Refund Policy"}</h2>
            <div className="text-gray-600 mb-6 space-y-3 leading-relaxed">
              {isScaleMode ? (
                <>
                  <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 mb-4">
                    <p className="text-sm font-bold text-blue-900">Generate up to 5,000 structured, high-quality rows for ₹299.</p>
                    <p className="text-xs text-blue-700 mt-1">You can further review, edit, and refine everything on our platform to match your exact needs.</p>
                  </div>
                  <p className="flex items-start gap-2">
                    <span className="mt-1 text-blue-500">•</span>
                    <span>We support <strong>text and numeric</strong> datasets only for now.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="mt-1 text-blue-500">•</span>
                    <span>Scaling will add more **similar rows** based on your current generation, not new types of data.</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="mt-1 text-blue-500">•</span>
                    <span>You will receive an email once your dataset is ready. This depends on complexity, but it will be as soon as possible.</span>
                  </p>
                  <p className="pt-2 text-xs border-t border-gray-100">
                    <span className="font-semibold text-gray-900">Refund Policy:</span> Don't worry! You will be refunded within 48 hours if we cannot process the request.
                  </p>
                </>
              ) : (
                <p>All credit purchases are final and non-refundable. We do not offer cancellations or exchanges as per our policy.</p>
              )}
            </div>
            <label className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl mb-6 cursor-pointer hover:bg-blue-100 transition-colors border border-blue-100">
              <input
                type="checkbox"
                className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={agreeToTerms}
                onChange={() => setAgreeToTerms(!agreeToTerms)}
              />
              <span className="text-sm text-blue-900 font-medium">
                {isScaleMode
                  ? "I understand and agree to the above details."
                  : "I understand that all credit purchases are final."
                }
              </span>
            </label>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowPopup(false)}
                className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPopup(false);
                  createOrder();
                }}
                className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all ${agreeToTerms ? "bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/25" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                disabled={!agreeToTerms}
              >
                Agree & Buy
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RazorpayCheckout;
